import { Box, Divider, FormControlLabel, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { attackTechniqueKeys } from "../../../api/queryKeys";
import ToggleButton from "../../../components/buttons/ToggleButton";
import ToggleGroup from "../../../components/buttons/ToggleGroup";
import CustomSwitch from "../../../components/CustomSwitch";
import ClearFiltersButton from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import SearchFilter from "../../../components/filters/SearchFilter";
import SearchInput from "../../../components/filters/SearchInput";
import LoadingIcon from "../../../components/LoadingIcon";
import TimePointSelector from "../../../components/TimePointSelector";
import { LATEST, ThreatTypes } from "../../../utils/constants";
import FrameworkOverviewGauge from "../frameworks/components/FrameworkOverviewGauge";
import AttackTactic from "./components/AttackTactic";
import ExportMitreButton from "./components/ExportMitreButton";
import ExportMitrePdfButton from "./components/ExportMitrePdfButton";
import TechniqueDrawer from "./components/TechniqueDrawer";

// MITRE ATT&CK order as per https://attack.mitre.org/
const MITRE_ORDER = [
  "Reconnaissance",
  "Resource Development",
  "Initial Access",
  "Execution",
  "Persistence",
  "Privilege Escalation",
  "Defense Evasion",
  "Credential Access",
  "Discovery",
  "Lateral Movement",
  "Collection",
  "Command and Control",
  "Exfiltration",
  "Impact",
];

const NOT_APPLICABLE = 0;

export default function MitreView() {
  const colors = useColors();

  const { getMitreAttackTechniques, getMitreHistory, trackEvent } = useAPI();

  // State management
  const [showNA, setShowNA] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRisk, setShowRisk] = useState(false);
  const [maxTacticSectionHeight, setMaxTacticSectionHeight] =
    useState("1000px");
  const [maxTacticSectionWidth, setMaxTacticSectionWidth] = useState("100%");
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  // Time point management
  const [timePoint, setTimePoint] = useState(LATEST);
  const [selectedMitreData, setSelectedMitreData] = useState(null);

  // Filter state
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    threatTypes: {},
    tactics: {},
    threatGroups: {},
    countries: {},
    industries: {},
  });

  // Data queries
  const { data: tactics, isLoading: tacticsLoading } = useQuery({
    queryKey: attackTechniqueKeys.all,
    queryFn: getMitreAttackTechniques,
    select: (response) => response.data,
  });

  const { data: mitreHistory, isLoading: isLoadingMitreHistory } = useQuery({
    queryKey: ["mitreHistory"],
    queryFn: getMitreHistory,
    select: (response) => response.data,
  });

  // Helper functions
  const addTechniqueToTacticMap = useCallback(
    (tacticMap, technique, tactic) => {
      if (!tacticMap.has(tactic.tactic_id)) {
        tacticMap.set(tactic.tactic_id, {
          title: tactic.tactic_name,
          techniques: [],
          percentage: 0,
        });
      }

      tacticMap.get(tactic.tactic_id).techniques.push({
        name: technique.technique_name,
        id: technique.technique_id,
        percentage: technique.entities_coverage,
        applicable: technique.related_step_ids?.length > 0 || false,
        ...technique,
      });
    },
    []
  );

  const transformTechniques = useCallback(
    (techniques) => {
      if (!techniques) return [];

      const tacticMap = new Map();

      techniques.forEach((technique) => {
        technique.related_attack_tactics?.forEach((tactic) => {
          // Add only techniques that are not sub-techniques
          if (!technique.technique_id.includes(".")) {
            addTechniqueToTacticMap(tacticMap, technique, tactic);
          }
        });
      });

      return Array.from(tacticMap.values());
    },
    [addTechniqueToTacticMap]
  );

  const calculatePercentages = useCallback((tactics) => {
    if (!tactics?.length) return NOT_APPLICABLE;

    let mitreCoverageSum = 0;
    let totalTechniques = 0;

    tactics.forEach((tactic) => {
      let techniqueCoverageSum = 0;
      const applicableTechniquesInTactic = tactic.techniques.filter(
        (technique) => technique.applicable
      );

      for (const technique of applicableTechniquesInTactic) {
        techniqueCoverageSum += technique.percentage;
      }

      if (applicableTechniquesInTactic.length > 0) {
        tactic.applicable = true;
        tactic.percentage = (
          techniqueCoverageSum / applicableTechniquesInTactic.length
        ).toFixed(0);
        totalTechniques += applicableTechniquesInTactic.length;
      } else {
        tactic.applicable = false;
        tactic.percentage = NOT_APPLICABLE;
      }
      mitreCoverageSum += techniqueCoverageSum;
    });

    // If no applicable techniques, return N/A
    let applicableTactics = tactics.filter((tactic) => tactic.applicable);
    if (applicableTactics.length > 0) {
      return (mitreCoverageSum / totalTechniques).toFixed(0);
    } else {
      return NOT_APPLICABLE;
    }
  }, []);

  // Computed values using useMemo to prevent infinite renders
  const currentTechniqueData = useMemo(() => {
    const isHistoricalView = timePoint !== LATEST;
    const sourceData = isHistoricalView ? selectedMitreData : tactics;
    return sourceData;
  }, [timePoint, selectedMitreData, tactics]);

  const displayTactics = useMemo(() => {
    const transformedTactics = transformTechniques(currentTechniqueData);
    return transformedTactics.sort(
      (a, b) => MITRE_ORDER.indexOf(a.title) - MITRE_ORDER.indexOf(b.title)
    );
  }, [currentTechniqueData, transformTechniques]);

  const filterOptions = useMemo(() => {
    if (!displayTactics?.length) {
      return {
        threatTypes: ThreatTypes,
        tactics: MITRE_ORDER,
        threatGroups: [],
        countries: [],
        industries: [],
      };
    }

    const threatGroups = new Set();
    const countries = new Set();
    const industries = new Set();

    displayTactics.forEach((tactic) => {
      tactic.techniques.forEach((technique) => {
        technique.related_attack_groups?.forEach((group) => {
          threatGroups.add(group.name);
        });
        technique.related_countries?.forEach((country) => {
          countries.add(country);
        });
        technique.related_industries?.forEach((industry) => {
          industries.add(industry);
        });
      });
    });

    return {
      threatTypes: ThreatTypes,
      tactics: MITRE_ORDER,
      threatGroups: Array.from(threatGroups).sort(),
      countries: Array.from(countries).sort(),
      industries: Array.from(industries).sort(),
    };
  }, [displayTactics]);

  const filteredTechniques = useMemo(() => {
    if (!displayTactics?.length) return [];

    let filtered = [...displayTactics];

    // Apply tactic filter
    if (Object.keys(appliedFilters.tactics).length > 0) {
      const tacticsFilter = Object.keys(appliedFilters.tactics).filter(
        (key) => appliedFilters.tactics[key] === true
      );
      if (tacticsFilter.length > 0) {
        filtered = filtered.filter((tactic) =>
          tacticsFilter.includes(tactic.title)
        );
      }
    }

    // Apply threat types filter
    if (Object.keys(appliedFilters.threatTypes).length > 0) {
      const threatTypesFilter = Object.keys(appliedFilters.threatTypes).filter(
        (key) => appliedFilters.threatTypes[key] === true
      );
      if (threatTypesFilter.length > 0) {
        filtered = filtered
          .map((tactic) => ({
            ...tactic,
            techniques: tactic.techniques?.filter((technique) =>
              technique.related_threat_types?.some((threatType) =>
                threatTypesFilter.includes(threatType)
              )
            ),
          }))
          .filter((tactic) => tactic.techniques.length > 0);
      }
    }

    // Apply threat groups filter
    if (Object.keys(appliedFilters.threatGroups).length > 0) {
      const threatGroupsFilter = Object.keys(
        appliedFilters.threatGroups
      ).filter((key) => appliedFilters.threatGroups[key] === true);
      if (threatGroupsFilter.length > 0) {
        filtered = filtered
          .map((tactic) => ({
            ...tactic,
            techniques: tactic.techniques?.filter(
              (technique) =>
                (showNA || technique.applicable) &&
                technique.related_attack_groups?.some((threatGroup) =>
                  threatGroupsFilter.includes(threatGroup.name)
                )
            ),
          }))
          .filter((tactic) => tactic.techniques.length > 0);
      }
    }

    // Apply search string filter
    if (appliedFilters.searchString !== "") {
      const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
      filtered = filtered
        .map((tactic) => ({
          ...tactic,
          techniques: tactic.techniques?.filter((technique) =>
            technique.name.toLowerCase().includes(lowerCaseSearchString)
          ),
        }))
        .filter(
          (tactic) =>
            tactic.title.toLowerCase().includes(lowerCaseSearchString) ||
            tactic.techniques.length > 0
        );
    }

    // Apply countries filter
    if (Object.keys(appliedFilters.countries).length > 0) {
      const countriesFilter = Object.keys(appliedFilters.countries).filter(
        (key) => appliedFilters.countries[key] === true
      );
      if (countriesFilter.length > 0) {
        filtered = filtered
          .map((tactic) => ({
            ...tactic,
            techniques: tactic.techniques?.filter((technique) =>
              technique.related_countries?.some((country) =>
                countriesFilter.includes(country)
              )
            ),
          }))
          .filter((tactic) => tactic.techniques.length > 0);
      }
    }

    // Apply industries filter
    if (Object.keys(appliedFilters.industries).length > 0) {
      const industriesFilter = Object.keys(appliedFilters.industries).filter(
        (key) => appliedFilters.industries[key] === true
      );
      if (industriesFilter.length > 0) {
        filtered = filtered
          .map((tactic) => ({
            ...tactic,
            techniques: tactic.techniques?.filter((technique) =>
              technique.related_industries?.some((industry) =>
                industriesFilter.includes(industry)
              )
            ),
          }))
          .filter((tactic) => tactic.techniques.length > 0);
      }
    }

    return filtered;
  }, [displayTactics, appliedFilters, showNA]);

  const mitreTotalPercentage = useMemo(() => {
    return Number(calculatePercentages(filteredTechniques));
  }, [filteredTechniques, calculatePercentages]);

  const isHistoricalView = useMemo(() => timePoint !== LATEST, [timePoint]);

  // Event handlers
  const handleTimePointClick = useCallback(
    (label, option) => {
      setTimePoint(label);

      if (label === LATEST) {
        setSelectedMitreData(null);
      } else {
        // The option IS the mitre_data array directly, not an object containing it
        setSelectedMitreData(option);
      }

      trackEvent(`User changed time range to ${label} on MITRE ATT&CK`);
    },
    [trackEvent]
  );

  const handleTechniqueClick = useCallback(
    (technique) => {
      // Disable drawer for historical views
      if (isHistoricalView) {
        return;
      }

      const message = `Open technique drawer: ${technique.name}`;
      trackEvent(message);
      setSearchParams({ technique: technique.id });
    },
    [isHistoricalView, trackEvent, setSearchParams]
  );

  const findTechnique = useCallback(
    (techniqueId) => {
      if (!displayTactics) return null;

      for (const tactic of displayTactics) {
        for (const technique of tactic.techniques) {
          if (technique.id === techniqueId) {
            return technique;
          }
        }
      }
      return null;
    },
    [displayTactics]
  );

  const switchChanged = useCallback(
    (event) => {
      let checked = event.target.checked;
      setShowNA(checked);
      trackEvent("User toggled Show N/A on MITRE ATT&CK to " + checked);
    },
    [trackEvent]
  );

  // Effects
  useEffect(() => {
    if (searchParams.has("technique") && displayTactics) {
      let technique = findTechnique(searchParams.get("technique"));
      setSelectedTechnique(technique);
    } else {
      setSelectedTechnique(null);
    }
  }, [searchParams, displayTactics, findTechnique]);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const dynamicMaxHeight = `${window.innerHeight - 300}px`;
        const dynamicMaxWidth = `${window.innerWidth - 130}px`;

        setMaxTacticSectionHeight(dynamicMaxHeight);
        setMaxTacticSectionWidth(dynamicMaxWidth);
      }
    };

    updateDimensions();

    // Create ResizeObserver instance
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    // Start observing the container
    resizeObserver.observe(document.documentElement);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const content = (
    <>
      {tacticsLoading && <LoadingIcon />}
      {!tacticsLoading && (
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Header */}
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            gap={3}
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: colors.common_white,
              border: `1px solid ${colors.divider}`,
            }}
          >
            {/* Left: Overall Score + Gauge */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
              minWidth="120px"
            >
              <FrameworkOverviewGauge
                value={mitreTotalPercentage}
                invertColorScale={showRisk}
              />
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: colors.divider }}
            />

            {/* Right: Filters + Actions */}
            <Box display="flex" flexDirection="column" gap={1.5} flex={1}>
              {/* Row 1: Filter chips + Show N/A + Clear Filters + Coverage/Risk */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <SearchFilter
                    filterName={"Tactic"}
                    fieldName={"tactics"}
                    options={filterOptions.tactics.map((tactic) => ({
                      name: tactic,
                      label: tactic,
                      displayName: tactic,
                    }))}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.tactics}
                  />
                  <SearchFilter
                    filterName={"Threat Actor"}
                    fieldName={"threatGroups"}
                    options={filterOptions.threatGroups.map((threatGroup) => ({
                      name: threatGroup,
                      label: threatGroup,
                      displayName: threatGroup,
                    }))}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.threatGroups}
                  />
                  <FilterMenu
                    filterName={"Threat Type"}
                    fieldName={"threatTypes"}
                    options={filterOptions.threatTypes.map((threatType) => ({
                      name: threatType,
                      label: threatType,
                      displayName: threatType,
                    }))}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.threatTypes}
                  />
                  <SearchFilter
                    filterName={"Target Country"}
                    fieldName={"countries"}
                    options={filterOptions.countries.map((country) => ({
                      name: country,
                      label: country,
                      displayName: country,
                    }))}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.countries}
                  />
                  <SearchFilter
                    filterName={"Industry"}
                    fieldName={"industries"}
                    options={filterOptions.industries.map((industry) => ({
                      name: industry,
                      label: industry,
                      displayName: industry,
                    }))}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.industries}
                  />
                  <FormControlLabel
                    sx={{
                      background: colors.common_white,
                      color: colors.text_secondary,
                      height: "32px",
                      border: `1px solid ${colors.chip_enabled_stroke}`,
                      textTransform: "unset",
                      borderRadius: "10px",
                      margin: "0",
                      padding: "0 6px",
                      "& .MuiFormControlLabel-label": {
                        marginRight: "8px",
                      },
                    }}
                    control={
                      <CustomSwitch checked={showNA} onChange={switchChanged} />
                    }
                    label={
                      <Typography variant="caption" sx={{ padding: "0 6px" }}>
                        Show N/A
                      </Typography>
                    }
                    labelPlacement="start"
                  />
                  <ClearFiltersButton
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                  />
                </Box>

                {/* Coverage/Risk Toggle */}
                <ToggleGroup>
                  <ToggleButton
                    selected={!showRisk}
                    onClick={() => {
                      if (showRisk) {
                        trackEvent(
                          `User toggled to show Coverage on MITRE ATT&CK`
                        );
                        setShowRisk(false);
                      }
                    }}
                  >
                    Coverage
                  </ToggleButton>
                  <ToggleButton
                    selected={showRisk}
                    onClick={() => {
                      if (!showRisk) {
                        trackEvent(`User toggled to show Risk on MITRE ATT&CK`);
                        setShowRisk(true);
                      }
                    }}
                  >
                    Risk
                  </ToggleButton>
                </ToggleGroup>
              </Box>

              {/* Row 2: Search + Time Selector + Export Buttons */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Box display="flex" gap={1} alignItems="center">
                  <SearchInput
                    fieldName={"searchString"}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    sx={{ width: "268px" }}
                  />
                  <TimePointSelector
                    history={mitreHistory}
                    isLoadingHistory={isLoadingMitreHistory}
                    selectedTimePoint={timePoint}
                    handleTimePointClick={handleTimePointClick}
                    loadingIconSize={8}
                    borderColor={colors.enabled_stroke}
                    backgroundColor={colors.common_white}
                    tooltipText="View MITRE ATT&CK as it was on this specific day"
                    gap={1}
                    padding={0.25}
                  />
                </Box>
                <Box display="flex" gap={1}>
                  <ExportMitreButton techniques={filteredTechniques} />
                  <ExportMitrePdfButton containerRef={contentRef} />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Techniques */}
          <Box
            ref={contentRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              width: maxTacticSectionWidth,
              whiteSpace: "nowrap",
              height: "100%",
              maxHeight: maxTacticSectionHeight,
            }}
          >
            {filteredTechniques.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "200px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: colors.text_secondary }}
                >
                  No data available
                </Typography>
              </Box>
            ) : (
              filteredTechniques.map((tactic, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "inline-block",
                    marginRight: 2,
                    marginLeft: "2px",
                    marginTop: "6px",
                  }}
                >
                  <AttackTactic
                    {...tactic}
                    showNA={showNA}
                    onTechniqueClick={handleTechniqueClick}
                    showRisk={showRisk}
                    isHistoricalView={isHistoricalView}
                  />
                </Box>
              ))
            )}
          </Box>

          {!isHistoricalView && (
            <TechniqueDrawer
              selectedTechnique={selectedTechnique}
              showRisk={showRisk}
            />
          )}
        </Box>
      )}
    </>
  );

  return content;
}
